import { isEmpty, isUrlValid } from '@jjj/components';
import { v4 as uuidv4 } from 'uuid';
import { keyBy } from 'src/helpers/core';
import { t } from 'src/i18n';
import { IChangePropertyPayload, IField } from '@dash/editForm';
import actionsTypes from './actionTypes';
import {
  Attribute,
  IOrganizationState,
  IFieldTypes,
  IFormName,
  ILoadDataSuccessPayload,
  ILoadAdminManagerSuccessPayload,
  IUpdateAdminManagerRequestPayload,
  ITouchPayload,
  IInvoiceSortingRule,
  IInvoiceSortingRuleAttribute,
  IInvoiceSortingRuleRow,
  IInvoiceSortingRulesFormData,
  ILoadInvoiceSortingRulesFormDataSuccessPayload,
  IRemoveInvoiceSortingRulePayload,
  IChangeInvoiceSortingRulePayload,
  IMoveInvoiceSortingRulePayload,
  IUpdateInvoiceSortingRulesSuccessPayload,
} from './types';
import { createReducer, getError } from '../../../helpers/reducer';

function isValidPEMCertificate(pem?: string) {
  const pemRegex = /-----BEGIN CERTIFICATE-----[\s\S]+?-----END CERTIFICATE-----/;

  if (!pem) {
    return true;
  }

  return pemRegex.test(pem.trim());
}

function validate(attr: Attribute, value: IFieldTypes) {
  let errorMessage = '';
  const requiredAttributes: Attribute[] = [
    'idp_certificate',
    'idp_entity_id',
    'ncns_period',
    'min_allowed_time_to_book',
    'before_clock_in',
    'flagged_timesheets_auto_approval_hours',
  ];

  if (requiredAttributes.includes(attr)) {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t(`dash.admin.organization.fields.${attr}_label`),
      });
    }
  }

  if (attr === 'idp_certificate' && !isValidPEMCertificate(value as string) && !isEmpty(value)) {
    errorMessage = t('dash.validation.valid_error', {
      fieldName: t('dash.admin.organization.fields.idp_certificate_label'),
    });
  }

  if (attr === 'idp_entity_id' && !isUrlValid(value) && !isEmpty(value)) {
    errorMessage = t('dash.validation.valid_error', {
      fieldName: t('dash.admin.organization.fields.idp_entity_id_label'),
    });
  }

  // Validate numeric fields
  if (
    ['ncns_period', 'min_allowed_time_to_book', 'before_clock_in', 'flagged_timesheets_auto_approval_hours'].includes(
      attr,
    )
  ) {
    if (!isEmpty(value) && (Number.isNaN(Number(value)) || Number(value) < 0)) {
      errorMessage = t('dash.validation.valid_error', {
        fieldName: t(`dash.admin.organization.fields.${attr}_label`),
      });
    }
  }

  return errorMessage;
}

const UPDATE_INVOICE_SORTING_RULES_ACTION_KEY = 'update_invoice_sorting_rules';

const initialInvoiceSortingRulesFormData: IInvoiceSortingRulesFormData = { fields: [], directions: [] };

function getInvoiceSortingRuleField(
  attr: IInvoiceSortingRuleAttribute,
  value: string | null = null,
  touched = false,
): IField<string | null> {
  return {
    name: attr,
    value,
    touched,
    error: value
      ? ''
      : t('dash.validation.select_a_field', {
          fieldName: t(`dash.admin.organization.invoices_configuration.${attr}_label`),
        }),
  };
}

function buildInvoiceSortingRuleRow(rule: IInvoiceSortingRule, id?: string): IInvoiceSortingRuleRow {
  return {
    id: id || uuidv4(),
    field: getInvoiceSortingRuleField('field', rule.field.id),
    direction: getInvoiceSortingRuleField('direction', rule.direction.id),
  };
}

const getInitialField = (attr: Attribute, value: any = null) => ({
  name: attr,
  value,
  touched: false,
  error: validate(attr, value),
});

const initialEditForm = {
  allow_admins_hours_review: getInitialField('allow_admins_hours_review', false),
  allow_managers_hours_review: getInitialField('allow_managers_hours_review', false),
  worker_submissions_auto_resolve_scenario: getInitialField('allow_managers_hours_review', 'none'),
  worker_submissions_auto_resolve_interval: getInitialField('worker_submissions_auto_resolve_interval', null),
};

const initialAdminManagerEditForm = {
  isAllowedToEdit: getInitialField('isAllowedToEdit', false),
  enabled: getInitialField('enabled', false),
  assertion_consumer_service_url: getInitialField('assertion_consumer_service_url', undefined),
  idp_certificate: getInitialField('idp_certificate', undefined),
  idp_entity_id: getInitialField('idp_entity_id', undefined),
  sp_entity_id: getInitialField('sp_entity_id', undefined),
};

const initialAgencyWorkerEditForm = {
  isAllowedToEdit: getInitialField('isAllowedToEdit', true),
  twilio_messages_disabled: getInitialField('twilio_messages_disabled', false),
  notification_type: getInitialField('notification_type', 'all'),
  ids: getInitialField('ids', ''),
};

const initialBookingFulfillmentEditForm = {
  isAllowedToEdit: getInitialField('isAllowedToEdit', true),
  request_specific_talent_enabled: getInitialField('request_specific_talent_enabled', 'my_team'),
};

const initialClockInOutEditForm = {
  isAllowedToEdit: getInitialField('isAllowedToEdit', true),
  allow_managers_to_manually_clock_in_clock_out_enabled: getInitialField(
    'allow_managers_to_manually_clock_in_clock_out_enabled',
    false,
  ),
};

const initialAgenciesConfigurationsEditForm = {
  edit_past_consolidated_jobs_enabled: getInitialField('edit_past_consolidated_jobs_enabled', false),
};

const initialGlobalConfigurationsEditForm = {
  isAllowedToEdit: getInitialField('isAllowedToEdit', true),
  allow_admins_hours_review: getInitialField('allow_admins_hours_review', false),
  allow_managers_hours_review: getInitialField('allow_managers_hours_review', false),
  ncns_period: getInitialField('ncns_period', 60),
  min_allowed_time_to_book: getInitialField('min_allowed_time_to_book', 90),
  before_clock_in: getInitialField('before_clock_in', 10),
  flagged_timesheets_approval_settings: getInitialField('flagged_timesheets_approval_settings', 'manual'),
  flagged_timesheets_auto_approval_hours: getInitialField('flagged_timesheets_auto_approval_hours', null),
  worker_submissions_auto_resolve_scenario: getInitialField('worker_submissions_auto_resolve_scenario', 'none'),
  worker_submissions_auto_resolve_interval: getInitialField('worker_submissions_auto_resolve_interval', null),
  csv_booking_filling_enabled: getInitialField('csv_booking_filling_enabled', false),
  workers_bulk_upload_enabled: getInitialField('workers_bulk_upload_enabled', false),
};

const initState: IOrganizationState = {
  isLoading: false,
  isAdminManagerLoading: false,
  isOrganizationUpdating: false,
  isAdminConfigurationUpdating: false,
  isManagerConfigurationUpdating: false,
  isAgencyWorkerConfigurationUpdating: false,
  isAgenciesConfigurationsUpdating: false,
  isBookingFulfillmentUpdating: false,
  isClockInOutUpdating: false,
  isNCNSPeriodUpdating: false,
  isHoursApprovalUpdating: false,
  isMinimumBookingTimeUpdating: false,
  isClockInPeriodUpdating: false,
  isFlaggedTimesheetsUpdating: false,
  isBulkJobsUploadUpdating: false,
  isWorkersBulkUploadUpdating: false,
  isInvoiceSortingRulesFormDataLoading: false,
  isInvoiceSortingRulesUpdating: false,
  isAllowedToUpdateInvoiceSortingRules: false,
  invoiceSortingRules: [],
  invoiceSortingRulesFormData: initialInvoiceSortingRulesFormData,
  editForm: initialEditForm,
  adminEditForm: initialAdminManagerEditForm,
  managerEditForm: initialAdminManagerEditForm,
  agencyWorkerEditForm: initialAgencyWorkerEditForm,
  agenciesConfigurationsEditForm: initialAgenciesConfigurationsEditForm,
  bookingFulfillmentEditForm: initialBookingFulfillmentEditForm,
  clockInOutEditForm: initialClockInOutEditForm,
  globalConfigurationsEditForm: initialGlobalConfigurationsEditForm,
  settings: undefined,
};

function onTouch(state: IOrganizationState, { payload }: { payload: ITouchPayload }) {
  const { formName, editForm } = payload;
  const newEditForm = JSON.stringify(editForm);

  return {
    ...state,
    [formName]: JSON.parse(newEditForm.replace(/"touched":false/g, '"touched":true')),
  };
}

function onChangeProperty(
  state: IOrganizationState,
  { payload }: { payload: IChangePropertyPayload<Attribute, IFieldTypes, IFormName> },
): IOrganizationState {
  const { attr, value, formName } = payload;
  const { editForm } = state;

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, value),
    name: attribute,
    touched: true,
  });

  if (formName) {
    return {
      ...state,
      [formName]: {
        ...state[formName],
        [attr]: getValue(value, attr),
      },
    };
  }

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onUpdateRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isOrganizationUpdating: true,
  };
}

function onUpdateSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isOrganizationUpdating: false,
    settings: {
      ...state.settings,
      allow_admins_hours_review: !!state.editForm.allow_admins_hours_review.value,
      allow_managers_hours_review: !!state.editForm.allow_managers_hours_review.value,
      worker_submissions_auto_resolve_scenario: state.editForm.worker_submissions_auto_resolve_scenario.value || 'none',
      worker_submissions_auto_resolve_interval: state.editForm.worker_submissions_auto_resolve_interval.value || null,
    },
  };
}

function onUpdateError(
  state: IOrganizationState,
  { payload }: { payload: { data: { [key: string]: string | string[] } } },
): IOrganizationState {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    (entry: [Attribute, IField]) => {
      return entry[0];
    },
    (entry: [Attribute, IField]) => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    isOrganizationUpdating: false,
    editForm,
  };
}

function onLoadSettingsRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isLoading: true,
  };
}

function onLoadSettingsSuccess(
  state: IOrganizationState,
  { payload }: { payload: ILoadDataSuccessPayload },
): IOrganizationState {
  const {
    editForm,
    globalConfigurationsEditForm,
    bookingFulfillmentEditForm,
    clockInOutEditForm,
    agencyWorkerEditForm,
    agenciesConfigurationsEditForm,
  } = state;
  const {
    allow_admins_hours_review,
    allow_managers_hours_review,
    worker_submissions_auto_resolve_scenario,
    worker_submissions_auto_resolve_interval,
    edit_past_consolidated_jobs_enabled,
    organizations,
    organizations_countries,
    workers,
    invoice_sorting_rules,
    actions,
  } = payload;

  // Update organization settings
  editForm.allow_admins_hours_review.value = allow_admins_hours_review;
  editForm.allow_managers_hours_review.value = allow_managers_hours_review;
  editForm.worker_submissions_auto_resolve_scenario.value = worker_submissions_auto_resolve_scenario;
  editForm.worker_submissions_auto_resolve_interval.value = worker_submissions_auto_resolve_interval;

  // Update global configurations edit form
  if (organizations) {
    globalConfigurationsEditForm.ncns_period.value = organizations.ncns_period || 60;
  }

  // Update hours review settings
  globalConfigurationsEditForm.allow_admins_hours_review.value = allow_admins_hours_review;
  globalConfigurationsEditForm.allow_managers_hours_review.value = allow_managers_hours_review;

  // Update worker submissions settings
  globalConfigurationsEditForm.worker_submissions_auto_resolve_scenario.value =
    worker_submissions_auto_resolve_scenario;
  globalConfigurationsEditForm.worker_submissions_auto_resolve_interval.value =
    worker_submissions_auto_resolve_interval;

  // Set individual hours approval settings from organization_settings
  globalConfigurationsEditForm.allow_admins_hours_review.value = allow_admins_hours_review;
  globalConfigurationsEditForm.allow_managers_hours_review.value = allow_managers_hours_review;

  if (organizations_countries) {
    globalConfigurationsEditForm.min_allowed_time_to_book.value =
      organizations_countries.min_allowed_time_to_book || 90;
    globalConfigurationsEditForm.before_clock_in.value = organizations_countries.grace_period?.before_clock_in || 10;
    globalConfigurationsEditForm.csv_booking_filling_enabled.value =
      organizations_countries.csv_booking_filling_enabled || false;
    globalConfigurationsEditForm.workers_bulk_upload_enabled.value =
      organizations_countries.workers_bulk_upload_enabled || false;
  }

  // Update manager settings edit form
  if (organizations_countries) {
    bookingFulfillmentEditForm.request_specific_talent_enabled.value =
      (organizations_countries.request_specific_agency_enabled && 'agencies') ||
      (organizations_countries.request_specific_talent_enabled && 'my_team') ||
      'disable';
    clockInOutEditForm.allow_managers_to_manually_clock_in_clock_out_enabled.value =
      organizations_countries.allow_managers_to_manually_clock_in_clock_out_enabled || false;
  }

  // Update agencies configurations edit form
  agenciesConfigurationsEditForm.edit_past_consolidated_jobs_enabled.value =
    edit_past_consolidated_jobs_enabled || false;

  // Update agency worker configuration edit form
  if (workers) {
    const twilioMessagesDisabled = workers.twilio_messages_disabled || false;
    const workerIds = workers.ids || [];

    // Determine notification type based on whether ids exist and are not empty
    const hasSpecificIds = workerIds && workerIds.length > 0;
    const notificationType = hasSpecificIds ? 'specific' : 'all';

    // Set form values
    agencyWorkerEditForm.twilio_messages_disabled.value = twilioMessagesDisabled;
    agencyWorkerEditForm.notification_type.value = notificationType;
    agencyWorkerEditForm.ids.value = hasSpecificIds ? workerIds.join(', ') : '';
  }

  return {
    ...state,
    isLoading: false,
    editForm,
    globalConfigurationsEditForm,
    bookingFulfillmentEditForm,
    clockInOutEditForm,
    agencyWorkerEditForm,
    agenciesConfigurationsEditForm,
    isAllowedToUpdateInvoiceSortingRules: (actions || []).some(
      action => action.key === UPDATE_INVOICE_SORTING_RULES_ACTION_KEY,
    ),
    invoiceSortingRules: (invoice_sorting_rules || []).map(rule => buildInvoiceSortingRuleRow(rule)),
    settings: {
      allow_admins_hours_review,
      allow_managers_hours_review,
      worker_submissions_auto_resolve_scenario,
      worker_submissions_auto_resolve_interval,
      edit_past_consolidated_jobs_enabled,
    },
  };
}

function onLoadSettingsError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isLoading: false,
    isAllowedToUpdateInvoiceSortingRules: false,
  };
}

function onLoadAdminManagerSettingsRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAdminManagerLoading: true,
  };
}

function onLoadAdminManagerSettingsSuccess(
  state: IOrganizationState,
  { payload }: { payload: ILoadAdminManagerSuccessPayload },
): IOrganizationState {
  const { adminEditForm, managerEditForm } = state;
  const { admin_sso_protocol_setting, manager_sso_protocol_setting } = payload;

  const updatedAdminEditForm = {
    ...adminEditForm,
    isAllowedToEdit: {
      ...adminEditForm.isAllowedToEdit,
      value: admin_sso_protocol_setting.actions?.[0]?.method === 'put',
    },
    enabled: {
      ...adminEditForm.enabled,
      error: validate('enabled', admin_sso_protocol_setting.enabled),
      value: admin_sso_protocol_setting.enabled,
    },
    idp_entity_id: {
      ...adminEditForm.idp_entity_id,
      error: validate('idp_entity_id', admin_sso_protocol_setting.settings.idp_entity_id),
      value: admin_sso_protocol_setting.settings.idp_entity_id,
    },
    sp_entity_id: {
      ...adminEditForm.sp_entity_id,
      error: validate('sp_entity_id', admin_sso_protocol_setting.settings.sp_entity_id),
      value: admin_sso_protocol_setting.settings.sp_entity_id,
    },
    assertion_consumer_service_url: {
      ...adminEditForm.assertion_consumer_service_url,
      error: validate(
        'assertion_consumer_service_url',
        admin_sso_protocol_setting.settings.assertion_consumer_service_url,
      ),
      value: admin_sso_protocol_setting.settings.assertion_consumer_service_url,
    },
    idp_certificate: {
      ...adminEditForm.idp_certificate,
      error: validate('idp_certificate', admin_sso_protocol_setting.settings.idp_certificate),
      value: admin_sso_protocol_setting.settings.idp_certificate,
    },
  };

  const updatedManagerEditForm = {
    ...managerEditForm,
    isAllowedToEdit: {
      ...managerEditForm.isAllowedToEdit,
      value: manager_sso_protocol_setting.actions?.[0]?.method === 'put',
    },
    enabled: {
      ...managerEditForm.enabled,
      error: validate('enabled', manager_sso_protocol_setting.enabled),
      value: manager_sso_protocol_setting.enabled,
    },
    idp_entity_id: {
      ...managerEditForm.idp_entity_id,
      error: validate('idp_entity_id', manager_sso_protocol_setting.settings.idp_entity_id),
      value: manager_sso_protocol_setting.settings.idp_entity_id,
    },
    sp_entity_id: {
      ...managerEditForm.sp_entity_id,
      error: validate('sp_entity_id', manager_sso_protocol_setting.settings.sp_entity_id),
      value: manager_sso_protocol_setting.settings.sp_entity_id,
    },
    assertion_consumer_service_url: {
      ...managerEditForm.assertion_consumer_service_url,
      error: validate(
        'assertion_consumer_service_url',
        manager_sso_protocol_setting.settings.assertion_consumer_service_url,
      ),
      value: manager_sso_protocol_setting.settings.assertion_consumer_service_url,
    },
    idp_certificate: {
      ...managerEditForm.idp_certificate,
      error: validate('idp_certificate', manager_sso_protocol_setting.settings.idp_certificate),
      value: manager_sso_protocol_setting.settings.idp_certificate,
    },
  };

  return {
    ...state,
    isAdminManagerLoading: false,
    adminEditForm: updatedAdminEditForm,
    managerEditForm: updatedManagerEditForm,
  };
}

function onLoadAdminManagerSettingsError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAdminManagerLoading: false,
  };
}

function onUpdateAdminManagerRequest(
  state: IOrganizationState,
  { payload }: { payload: IUpdateAdminManagerRequestPayload },
): IOrganizationState {
  return {
    ...state,
    ...(payload.user_type === 'admin' && { isAdminConfigurationUpdating: true }),
    ...(payload.user_type === 'manager' && { isManagerConfigurationUpdating: true }),
  };
}

function onUpdateAdminManagerSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAdminConfigurationUpdating: false,
    isManagerConfigurationUpdating: false,
  };
}

function onUpdateAdminManagerError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAdminConfigurationUpdating: false,
    isManagerConfigurationUpdating: false,
  };
}

// Global Configurations Reducers
function onUpdateNCNSPeriodRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isNCNSPeriodUpdating: true,
  };
}

function onUpdateNCNSPeriodSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isNCNSPeriodUpdating: false,
  };
}

function onUpdateNCNSPeriodError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isNCNSPeriodUpdating: false,
  };
}

function onUpdateMinimumBookingTimeRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isMinimumBookingTimeUpdating: true,
  };
}

function onUpdateMinimumBookingTimeSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isMinimumBookingTimeUpdating: false,
  };
}

function onUpdateMinimumBookingTimeError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isMinimumBookingTimeUpdating: false,
  };
}

function onUpdateClockInPeriodRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isClockInPeriodUpdating: true,
  };
}

function onUpdateClockInPeriodSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isClockInPeriodUpdating: false,
  };
}

function onUpdateClockInPeriodError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isClockInPeriodUpdating: false,
  };
}

function onUpdateFlaggedTimesheetsRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isFlaggedTimesheetsUpdating: true,
  };
}

function onUpdateFlaggedTimesheetsSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isFlaggedTimesheetsUpdating: false,
  };
}

function onUpdateFlaggedTimesheetsError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isFlaggedTimesheetsUpdating: false,
  };
}

function onUpdateBulkJobsUploadRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isBulkJobsUploadUpdating: true,
  };
}

function onUpdateBulkJobsUploadSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isBulkJobsUploadUpdating: false,
  };
}

function onUpdateBulkJobsUploadError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isBulkJobsUploadUpdating: false,
  };
}

function onUpdateWorkersBulkUploadRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isWorkersBulkUploadUpdating: true,
  };
}

function onUpdateWorkersBulkUploadSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isWorkersBulkUploadUpdating: false,
  };
}

function onUpdateWorkersBulkUploadError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isWorkersBulkUploadUpdating: false,
  };
}

// Manager Settings Reducers
function onUpdateBookingFulfillmentRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isBookingFulfillmentUpdating: true,
  };
}

function onUpdateBookingFulfillmentSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isBookingFulfillmentUpdating: false,
  };
}

function onUpdateBookingFulfillmentError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isBookingFulfillmentUpdating: false,
  };
}

function onUpdateClockInOutRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isClockInOutUpdating: true,
  };
}

function onUpdateClockInOutSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isClockInOutUpdating: false,
  };
}

function onUpdateClockInOutError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isClockInOutUpdating: false,
  };
}

function onUpdateAgenciesConfigurationsRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAgenciesConfigurationsUpdating: true,
  };
}

function onUpdateAgenciesConfigurationsSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAgenciesConfigurationsUpdating: false,
  };
}

function onUpdateAgenciesConfigurationsError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAgenciesConfigurationsUpdating: false,
  };
}

// Invoices Configuration Reducers
function onLoadInvoiceSortingRulesFormDataRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isInvoiceSortingRulesFormDataLoading: true,
  };
}

function onLoadInvoiceSortingRulesFormDataSuccess(
  state: IOrganizationState,
  { payload }: { payload: ILoadInvoiceSortingRulesFormDataSuccessPayload },
): IOrganizationState {
  const { fields, directions } = payload;

  return {
    ...state,
    isInvoiceSortingRulesFormDataLoading: false,
    invoiceSortingRulesFormData: { fields: fields || [], directions: directions || [] },
  };
}

function onLoadInvoiceSortingRulesFormDataError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isInvoiceSortingRulesFormDataLoading: false,
  };
}

function onAddInvoiceSortingRule(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    invoiceSortingRules: [
      ...state.invoiceSortingRules,
      {
        id: uuidv4(),
        field: getInvoiceSortingRuleField('field'),
        direction: getInvoiceSortingRuleField('direction'),
      },
    ],
  };
}

function onRemoveInvoiceSortingRule(
  state: IOrganizationState,
  { payload }: { payload: IRemoveInvoiceSortingRulePayload },
): IOrganizationState {
  return {
    ...state,
    invoiceSortingRules: state.invoiceSortingRules.filter((_rule, index) => index !== payload.index),
  };
}

function onChangeInvoiceSortingRule(
  state: IOrganizationState,
  { payload }: { payload: IChangeInvoiceSortingRulePayload },
): IOrganizationState {
  const { index, attr, value } = payload;

  return {
    ...state,
    invoiceSortingRules: state.invoiceSortingRules.map((rule, ruleIndex) =>
      ruleIndex === index ? { ...rule, [attr]: getInvoiceSortingRuleField(attr, value, true) } : rule,
    ),
  };
}

function onMoveInvoiceSortingRule(
  state: IOrganizationState,
  { payload }: { payload: IMoveInvoiceSortingRulePayload },
): IOrganizationState {
  const { index, toIndex } = payload;

  if (toIndex < 0 || toIndex >= state.invoiceSortingRules.length) {
    return state;
  }

  const invoiceSortingRules = [...state.invoiceSortingRules];
  const [movedRule] = invoiceSortingRules.splice(index, 1);
  invoiceSortingRules.splice(toIndex, 0, movedRule);

  return {
    ...state,
    invoiceSortingRules,
  };
}

function onUpdateInvoiceSortingRulesRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isInvoiceSortingRulesUpdating: true,
  };
}

function onUpdateInvoiceSortingRulesSuccess(
  state: IOrganizationState,
  { payload }: { payload: IUpdateInvoiceSortingRulesSuccessPayload },
): IOrganizationState {
  return {
    ...state,
    isInvoiceSortingRulesUpdating: false,
    invoiceSortingRules: (payload.invoice_sorting_rules || []).map((rule, index) =>
      buildInvoiceSortingRuleRow(rule, state.invoiceSortingRules[index]?.id),
    ),
  };
}

function onUpdateInvoiceSortingRulesError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isInvoiceSortingRulesUpdating: false,
  };
}

// Agency Worker Configuration Reducers
function onUpdateAgencyWorkerRequest(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAgencyWorkerConfigurationUpdating: true,
  };
}

function onUpdateAgencyWorkerSuccess(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAgencyWorkerConfigurationUpdating: false,
  };
}

function onUpdateAgencyWorkerError(state: IOrganizationState): IOrganizationState {
  return {
    ...state,
    isAgencyWorkerConfigurationUpdating: false,
  };
}

export default createReducer(initState, {
  [actionsTypes.TOUCH]: onTouch,
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.UPDATE_REQUEST]: onUpdateRequest,
  [actionsTypes.UPDATE_SUCCESS]: onUpdateSuccess,
  [actionsTypes.UPDATE_ERROR]: onUpdateError,
  [actionsTypes.LOAD_REQUEST]: onLoadSettingsRequest,
  [actionsTypes.LOAD_SUCCESS]: onLoadSettingsSuccess,
  [actionsTypes.LOAD_ERROR]: onLoadSettingsError,
  [actionsTypes.LOAD_ADMIN_MANAGER_REQUEST]: onLoadAdminManagerSettingsRequest,
  [actionsTypes.LOAD_ADMIN_MANAGER_SUCCESS]: onLoadAdminManagerSettingsSuccess,
  [actionsTypes.LOAD_ADMIN_MANAGER_ERROR]: onLoadAdminManagerSettingsError,
  [actionsTypes.UPDATE_ADMIN_MANAGER_REQUEST]: onUpdateAdminManagerRequest,
  [actionsTypes.UPDATE_ADMIN_MANAGER_SUCCESS]: onUpdateAdminManagerSuccess,
  [actionsTypes.UPDATE_ADMIN_MANAGER_ERROR]: onUpdateAdminManagerError,
  // Global Configurations
  [actionsTypes.UPDATE_NCNS_PERIOD_REQUEST]: onUpdateNCNSPeriodRequest,
  [actionsTypes.UPDATE_NCNS_PERIOD_SUCCESS]: onUpdateNCNSPeriodSuccess,
  [actionsTypes.UPDATE_NCNS_PERIOD_ERROR]: onUpdateNCNSPeriodError,
  [actionsTypes.UPDATE_MINIMUM_BOOKING_TIME_REQUEST]: onUpdateMinimumBookingTimeRequest,
  [actionsTypes.UPDATE_MINIMUM_BOOKING_TIME_SUCCESS]: onUpdateMinimumBookingTimeSuccess,
  [actionsTypes.UPDATE_MINIMUM_BOOKING_TIME_ERROR]: onUpdateMinimumBookingTimeError,
  [actionsTypes.UPDATE_CLOCK_IN_PERIOD_REQUEST]: onUpdateClockInPeriodRequest,
  [actionsTypes.UPDATE_CLOCK_IN_PERIOD_SUCCESS]: onUpdateClockInPeriodSuccess,
  [actionsTypes.UPDATE_CLOCK_IN_PERIOD_ERROR]: onUpdateClockInPeriodError,
  [actionsTypes.UPDATE_FLAGGED_TIMESHEETS_REQUEST]: onUpdateFlaggedTimesheetsRequest,
  [actionsTypes.UPDATE_FLAGGED_TIMESHEETS_SUCCESS]: onUpdateFlaggedTimesheetsSuccess,
  [actionsTypes.UPDATE_FLAGGED_TIMESHEETS_ERROR]: onUpdateFlaggedTimesheetsError,
  [actionsTypes.UPDATE_BULK_JOBS_UPLOAD_REQUEST]: onUpdateBulkJobsUploadRequest,
  [actionsTypes.UPDATE_BULK_JOBS_UPLOAD_SUCCESS]: onUpdateBulkJobsUploadSuccess,
  [actionsTypes.UPDATE_BULK_JOBS_UPLOAD_ERROR]: onUpdateBulkJobsUploadError,
  [actionsTypes.UPDATE_WORKERS_BULK_UPLOAD_REQUEST]: onUpdateWorkersBulkUploadRequest,
  [actionsTypes.UPDATE_WORKERS_BULK_UPLOAD_SUCCESS]: onUpdateWorkersBulkUploadSuccess,
  [actionsTypes.UPDATE_WORKERS_BULK_UPLOAD_ERROR]: onUpdateWorkersBulkUploadError,
  // Manager Settings
  [actionsTypes.UPDATE_BOOKING_FULFILLMENT_REQUEST]: onUpdateBookingFulfillmentRequest,
  [actionsTypes.UPDATE_BOOKING_FULFILLMENT_SUCCESS]: onUpdateBookingFulfillmentSuccess,
  [actionsTypes.UPDATE_BOOKING_FULFILLMENT_ERROR]: onUpdateBookingFulfillmentError,
  [actionsTypes.UPDATE_CLOCK_IN_OUT_REQUEST]: onUpdateClockInOutRequest,
  [actionsTypes.UPDATE_CLOCK_IN_OUT_SUCCESS]: onUpdateClockInOutSuccess,
  [actionsTypes.UPDATE_CLOCK_IN_OUT_ERROR]: onUpdateClockInOutError,
  // Agencies Configurations
  [actionsTypes.UPDATE_AGENCIES_CONFIGURATIONS_REQUEST]: onUpdateAgenciesConfigurationsRequest,
  [actionsTypes.UPDATE_AGENCIES_CONFIGURATIONS_SUCCESS]: onUpdateAgenciesConfigurationsSuccess,
  [actionsTypes.UPDATE_AGENCIES_CONFIGURATIONS_ERROR]: onUpdateAgenciesConfigurationsError,
  // Invoices Configuration
  [actionsTypes.LOAD_INVOICE_SORTING_RULES_FORM_DATA_REQUEST]: onLoadInvoiceSortingRulesFormDataRequest,
  [actionsTypes.LOAD_INVOICE_SORTING_RULES_FORM_DATA_SUCCESS]: onLoadInvoiceSortingRulesFormDataSuccess,
  [actionsTypes.LOAD_INVOICE_SORTING_RULES_FORM_DATA_ERROR]: onLoadInvoiceSortingRulesFormDataError,
  [actionsTypes.ADD_INVOICE_SORTING_RULE]: onAddInvoiceSortingRule,
  [actionsTypes.REMOVE_INVOICE_SORTING_RULE]: onRemoveInvoiceSortingRule,
  [actionsTypes.CHANGE_INVOICE_SORTING_RULE]: onChangeInvoiceSortingRule,
  [actionsTypes.MOVE_INVOICE_SORTING_RULE]: onMoveInvoiceSortingRule,
  [actionsTypes.UPDATE_INVOICE_SORTING_RULES_REQUEST]: onUpdateInvoiceSortingRulesRequest,
  [actionsTypes.UPDATE_INVOICE_SORTING_RULES_SUCCESS]: onUpdateInvoiceSortingRulesSuccess,
  [actionsTypes.UPDATE_INVOICE_SORTING_RULES_ERROR]: onUpdateInvoiceSortingRulesError,
  // Agency Worker Configuration
  [actionsTypes.UPDATE_AGENCY_WORKER_REQUEST]: onUpdateAgencyWorkerRequest,
  [actionsTypes.UPDATE_AGENCY_WORKER_SUCCESS]: onUpdateAgencyWorkerSuccess,
  [actionsTypes.UPDATE_AGENCY_WORKER_ERROR]: onUpdateAgencyWorkerError,
});
